import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Badge } from 'reactstrap';
import {
  FileText, Eye, Edit, Calendar, TrendingUp, Tag, Folder, Star, Clock
} from 'react-feather';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import axios from 'axios';
import { baseURL } from '../../../../Services/api/baseURL';
import { H4, H3 } from '../../../../AbstractElements';
import Loader from '../../../Loader/Loader';
import moment from 'moment';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const BlogDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = JSON.parse(localStorage.getItem("token"));

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch blog statistics
      const statsResponse = await axios.get(`${baseURL}/api/blogs/stats`, {
        headers: { Authorization: `${token}` }
      });

      // Fetch recent blogs
      const blogsResponse = await axios.get(`${baseURL}/api/blogs/getAll?limit=5`, {
        headers: { Authorization: `${token}` }
      });

      setStats(statsResponse.data.data);
      setRecentBlogs(blogsResponse.data.blogs);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Chart configurations
  const blogStatusChartData = {
    labels: ['Published', 'Draft', 'Featured'],
    datasets: [
      {
        data: [
          stats?.publishedBlogs || 0,
          stats?.draftBlogs || 0,
          stats?.featuredBlogs || 0
        ],
        backgroundColor: [
          '#28a745',
          '#ffc107',
          '#007bff'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      },
    ],
  };

  const categoriesChartData = {
    labels: stats?.categories?.slice(0, 6).map(cat => cat._id || 'Uncategorized') || [],
    datasets: [
      {
        label: 'Posts per Category',
        data: stats?.categories?.slice(0, 6).map(cat => cat.count) || [],
        backgroundColor: [
          '#007bff',
          '#28a745',
          '#ffc107',
          '#dc3545',
          '#6f42c1',
          '#fd7e14'
        ],
        borderColor: [
          '#0056b3',
          '#1e7e34',
          '#e0a800',
          '#c82333',
          '#59379a',
          '#e8590c'
        ],
        borderWidth: 1
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  if (loading) {
    return (
      <Container fluid style={{ paddingTop: '30px', minHeight: '60vh' }}>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Loader />
        </div>
      </Container>
    );
  }

  return (
    <Container fluid style={{ paddingTop: '30px' }}>
      <Row className="mb-4">
        <Col xs={12}>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <H3>Blog Dashboard</H3>
              <p className="text-muted mb-0">Overview of your blog performance and content</p>
            </div>
            <div className="text-muted">
              <Calendar size={16} className="me-2" />
              Last updated: {moment().format('MMM DD, YYYY')}
            </div>
          </div>
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col xl={3} md={6} className="mb-3">
          <Card className="h-100 shadow-sm border-0">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-primary bg-opacity-10 p-3 rounded-circle">
                    <FileText className="text-primary" size={24} />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <div className="text-muted small">Total Blogs</div>
                  <H4 className="mb-0">{stats?.totalBlogs || 0}</H4>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xl={3} md={6} className="mb-3">
          <Card className="h-100 shadow-sm border-0">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-success bg-opacity-10 p-3 rounded-circle">
                    <Eye className="text-success" size={24} />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <div className="text-muted small">Published</div>
                  <H4 className="mb-0">{stats?.publishedBlogs || 0}</H4>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xl={3} md={6} className="mb-3">
          <Card className="h-100 shadow-sm border-0">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-warning bg-opacity-10 p-3 rounded-circle">
                    <Edit className="text-warning" size={24} />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <div className="text-muted small">Drafts</div>
                  <H4 className="mb-0">{stats?.draftBlogs || 0}</H4>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xl={3} md={6} className="mb-3">
          <Card className="h-100 shadow-sm border-0">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-info bg-opacity-10 p-3 rounded-circle">
                    <Star className="text-info" size={24} />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <div className="text-muted small">Featured</div>
                  <H4 className="mb-0">{stats?.featuredBlogs || 0}</H4>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Blog Status Chart */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm border-0">
            <CardBody>
              <H4 className="mb-3">
                <TrendingUp size={20} className="me-2" />
                Blog Status Distribution
              </H4>
              <div style={{ height: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Doughnut data={blogStatusChartData} options={chartOptions} />
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Categories Chart */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm border-0">
            <CardBody>
              <H4 className="mb-3">
                <Folder size={20} className="me-2" />
                Posts by Category
              </H4>
              <div style={{ height: '300px' }}>
                <Bar
                  data={categoriesChartData}
                  options={{
                    ...chartOptions,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          stepSize: 1
                        }
                      }
                    }
                  }}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Recent Blogs */}
        <Col lg={8} className="mb-4">
          <Card className="h-100 shadow-sm border-0">
            <CardBody>
              <H4 className="mb-3">
                <Clock size={20} className="me-2" />
                Recent Blog Posts
              </H4>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="bg-light">
                    <tr>
                      <th>Title</th>
                      <th>Status</th>
                      <th>Category</th>
                      <th>Created Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentBlogs.map((blog) => (
                      <tr key={blog._id}>
                        <td>
                          <div className="d-flex align-items-center">
                            {blog.images?.[0] && (
                              <img
                                src={blog.images[0]}
                                alt={blog.title}
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                  marginRight: '12px'
                                }}
                              />
                            )}
                            <div>
                              <div className="fw-medium">{blog.title}</div>
                              <small className="text-muted">
                                {blog.readTime || 'No read time'}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <Badge
                            color={
                              blog.status === 'active' ? 'success' :
                                blog.status === 'draft' ? 'warning' : 'danger'
                            }
                          >
                            {blog.status === 'active' ? 'Published' :
                              blog.status === 'draft' ? 'Draft' : 'Unpublished'}
                          </Badge>
                        </td>
                        <td>
                          {blog.category ? (
                            <Badge color="info" className="bg-opacity-10 text-info">
                              {blog.category}
                            </Badge>
                          ) : (
                            <span className="text-muted">Uncategorized</span>
                          )}
                        </td>
                        <td className="text-muted small">
                          {moment(blog.createdTime || blog.createdAt).format('MMM DD, YYYY')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Popular Tags & Quick Stats */}
        <Col lg={4} className="mb-4">
          <Card className="h-100 shadow-sm border-0">
            <CardBody>
              <H4 className="mb-3">
                <Tag size={20} className="me-2" />
                Popular Tags
              </H4>
              <div className="mb-4">
                {stats?.popularTags?.slice(0, 10).map((tag, index) => (
                  <Badge
                    key={index}
                    color="outline-primary"
                    className="me-2 mb-2"
                    style={{ fontSize: '12px' }}
                  >
                    {tag._id} ({tag.count})
                  </Badge>
                ))}
              </div>

              <H4 className="mb-3 mt-4">Quick Actions</H4>
              <div className="d-grid gap-2">
                <a
                  href="/blog"
                  className="btn btn-outline-primary btn-sm"
                >
                  <FileText size={16} className="me-2" />
                  View All Blogs
                </a>
                <a
                  href="/blog/create"
                  className="btn btn-primary btn-sm"
                >
                  <Edit size={16} className="me-2" />
                  Create New Blog
                </a>
              </div>

              <div className="mt-4 p-3 bg-light rounded">
                <h6 className="mb-2">Content Summary</h6>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <small>Total Words:</small>
                  <strong>
                    {recentBlogs.reduce((total, blog) => {
                      const wordCount = blog.content ?
                        blog.content.replace(/<[^>]*>/g, '').split(/\s+/).length : 0;
                      return total + wordCount;
                    }, 0).toLocaleString()}
                  </strong>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <small>Avg. Read Time:</small>
                  <strong>
                    {stats?.totalBlogs > 0 ?
                      Math.ceil(recentBlogs.reduce((total, blog) => {
                        const readTime = blog.readTime ?
                          parseInt(blog.readTime.split(' ')[0]) : 0;
                        return total + readTime;
                      }, 0) / recentBlogs.length) : 0} min
                  </strong>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BlogDashboard;